import { supabase } from './supabase';
import { logger, LogCategory } from './logger';
import { ChatWrapper, ChatMessage, ChatWrapperWithMessages } from '../types/chat';
import { ChatbotEnv } from '../types/chatbotEnv';
import { v4 as uuidv4 } from 'uuid';

/**
 * Service for managing chat operations
 */
class ChatService {
  private readonly TABLE_CHAT_WRAPPER = 'chat_wrappers';
  private readonly TABLE_CHAT_MESSAGES = 'chat_messages';

  /**
   * Create a new chat wrapper
   * @param userId The ID of the user creating the chat (user_id in database)
   * @param environment The chatbot environment
   * @param title Optional title for the chat (defaults to timestamp-based title)
   * @returns The created chat wrapper
   */
  async createChat(userId: string, environment: ChatbotEnv, title?: string): Promise<ChatWrapper | null> {
    try {
      const now = new Date();
      const chatId = uuidv4();
      
      // Generate a default title if none provided
      const chatTitle = title || `Chat ${now.toLocaleDateString()} ${now.toLocaleTimeString()}`;
      
      const chatWrapper: ChatWrapper = {
        id: chatId,
        user_id: userId,
        environment,
        title: chatTitle,
        last_message_date: now,
        created_at: now
      };
      
      const { data, error } = await supabase
        .from(this.TABLE_CHAT_WRAPPER)
        .insert(chatWrapper)
        .select()
        .single();
      
      if (error) {
        logger.error('Failed to create chat', LogCategory.DATABASE, { error, userId, environment });
        return null;
      }
      
      logger.info('Chat created successfully', LogCategory.DATABASE, { chatId, userId, environment });
      return data as ChatWrapper;
    } catch (error) {
      logger.error('Error creating chat', LogCategory.DATABASE, { error, userId, environment });
      return null;
    }
  }

  /**
   * Get a chat by its ID
   * @param chatId The ID of the chat to retrieve
   * @returns The chat wrapper if found, null otherwise
   */
  async getChatById(chatId: string): Promise<ChatWrapper | null> {
    try {
      const { data, error } = await supabase
        .from(this.TABLE_CHAT_WRAPPER)
        .select('*')
        .eq('id', chatId)
        .single();
      
      if (error) {
        logger.error('Failed to get chat by ID', LogCategory.DATABASE, { error, chatId });
        return null;
      }
      
      return data as ChatWrapper;
    } catch (error) {
      logger.error('Error getting chat by ID', LogCategory.DATABASE, { error, chatId });
      return null;
    }
  }

  /**
   * Get all chats for a user
   * @param userId The ID of the user
   * @param environment Optional environment filter
   * @returns Array of chat wrappers
   */
  async getUserChats(userId: string, environment?: ChatbotEnv): Promise<ChatWrapper[]> {
    try {
      let query = supabase
        .from(this.TABLE_CHAT_WRAPPER)
        .select('*')
        .eq('user_id', userId)
        .order('last_message_date', { ascending: false });
      
      // Apply environment filter if provided
      if (environment) {
        query = query.eq('environment', environment);
      }
      
      const { data, error } = await query;
      
      if (error) {
        logger.error('Failed to get user chats', LogCategory.DATABASE, { error, userId, environment });
        return [];
      }
      
      return data as ChatWrapper[];
    } catch (error) {
      logger.error('Error getting user chats', LogCategory.DATABASE, { error, userId });
      return [];
    }
  }

  /**
   * Add a message to a chat
   * @param chatWrapperId The ID of the chat to add the message to (chat_wrapper_id in database)
   * @param content The message content
   * @param isAiMessage Whether the message is from the AI (is_ai_message in database)
   * @param creatorId The ID of the message creator (creator_id in database, null for AI messages)
   * @param metadata Optional metadata for the message
   * @returns The created message if successful, null otherwise
   */
  async addMessage(
    chatWrapperId: string,
    content: string,
    isAiMessage: boolean,
    creatorId?: string,
    metadata?: any
  ): Promise<ChatMessage | null> {
    try {
      console.log("addMessage");
      const now = new Date();
      const messageId = uuidv4();
      
      const message: ChatMessage = {
        id: messageId,
        chat_wrapper_id: chatWrapperId,
        creator_id: isAiMessage ? undefined : creatorId,
        is_ai_message: isAiMessage,
        content,
        created_at: now,
        metadata
      };
      console.log("creating", message);

      // Validate Supabase session before proceeding with insert
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      // Check if session exists and is valid
      if (sessionError || !sessionData.session) {
        const errorMessage = 'Session validation failed: No active Supabase session';
        logger.error(errorMessage, LogCategory.SECURITY, {
          chatWrapperId,
          sessionError
        });
        throw Error("No Session");
      }
      
      // If session is valid, proceed with insert operation
      const { data, error } = await supabase
        .from(this.TABLE_CHAT_MESSAGES)
        .insert(message)
        .select()
        .single();

      console.log("created", data);

      if (error) {
        logger.error('Failed to add message', LogCategory.DATABASE, { error, chatWrapperId });
        return null;
      }
      
      // Update the last_message_date in the chat wrapper
      await this.updateChatLastMessageDate(chatWrapperId, now);
      
      logger.info('Message added successfully', LogCategory.DATABASE, { messageId, chatWrapperId });
      return data as ChatMessage;
    } catch (error) {
      logger.error('Error adding message', LogCategory.DATABASE, {
        error,
        chatWrapperId,
        isSessionError: error instanceof Error && error.message.includes('Session validation')
      });
      return null;
    }
  }

  /**
   * Update the last_message_date of a chat
   * @param chatId The ID of the chat to update
   * @param date The new last_message_date
   */
  private async updateChatLastMessageDate(chatId: string, date: Date): Promise<void> {
    try {
      const { error } = await supabase
        .from(this.TABLE_CHAT_WRAPPER)
        .update({ last_message_date: date })
        .eq('id', chatId);
      
      if (error) {
        logger.error('Failed to update chat last_message_date', LogCategory.DATABASE, { error, chatId });
      }
    } catch (error) {
      logger.error('Error updating chat last_message_date', LogCategory.DATABASE, { error, chatId });
    }
  }

  /**
   * Get all messages for a chat
   * @param chatWrapperId The ID of the chat (chat_wrapper_id in database)
   * @returns Array of chat messages
   */
  async getChatMessages(chatWrapperId: string): Promise<ChatMessage[]> {
    try {
      const { data, error } = await supabase
        .from(this.TABLE_CHAT_MESSAGES)
        .select('*')
        .eq('chat_wrapper_id', chatWrapperId)
        .order('created_at', { ascending: true });
      
      if (error) {
        logger.error('Failed to get chat messages', LogCategory.DATABASE, { error, chatWrapperId });
        return [];
      }
      
      return data as ChatMessage[];
    } catch (error) {
      logger.error('Error getting chat messages', LogCategory.DATABASE, { error, chatWrapperId });
      return [];
    }
  }

  /**
   * Get only new messages for a chat that are newer than the specified timestamp
   * @param chatWrapperId The ID of the chat (chat_wrapper_id in database)
   * @param afterTimestamp Only fetch messages created after this timestamp
   * @returns Array of new chat messages
   */
  async getNewChatMessages(chatWrapperId: string, afterTimestamp?: Date): Promise<ChatMessage[]> {
    try {
      // Start building the query
      let query = supabase
        .from(this.TABLE_CHAT_MESSAGES)
        .select('*')
        .eq('chat_wrapper_id', chatWrapperId)
        .order('created_at', { ascending: true });
      
      // Add timestamp filter if provided
      if (afterTimestamp) {
        query = query.gt('created_at', new Date(afterTimestamp).toISOString());
      }
      
      const { data, error } = await query;
      
      if (error) {
        logger.error('Failed to get new chat messages', LogCategory.DATABASE, {
          error,
          chatWrapperId,
        });
        return [];
      }
      
      logger.info('Retrieved new messages', LogCategory.DATABASE, {
        chatWrapperId,
        messageCount: data.length,
      });
      
      return data as ChatMessage[];
    } catch (error) {
      logger.error('Error getting new chat messages', LogCategory.DATABASE, {
        error,
        chatWrapperId,
      });
      return [];
    }
  }

  /**
   * Get a chat with all its messages
   * @param chatId The ID of the chat
   * @returns The chat with messages if found, null otherwise
   */
  async getChatWithMessages(chatId: string): Promise<ChatWrapperWithMessages | null> {
    try {
      const chat = await this.getChatById(chatId);
      if (!chat) {
        return null;
      }
      
      const messages = await this.getChatMessages(chatId);
      
      return {
        ...chat,
        messages
      };
    } catch (error) {
      logger.error('Error getting chat with messages', LogCategory.DATABASE, { error, chatId });
      return null;
    }
  }

  /**
   * Update a chat's title
   * @param chatId The ID of the chat to update
   * @param title The new title
   * @returns The updated chat if successful, null otherwise
   */
  async updateChatTitle(chatId: string, title: string): Promise<ChatWrapper | null> {
    try {
      const { data, error } = await supabase
        .from(this.TABLE_CHAT_WRAPPER)
        .update({ title })
        .eq('id', chatId)
        .select()
        .single();
      
      if (error) {
        logger.error('Failed to update chat title', LogCategory.DATABASE, { error, chatId });
        return null;
      }
      
      logger.info('Chat title updated successfully', LogCategory.DATABASE, { chatId, title });
      return data as ChatWrapper;
    } catch (error) {
      logger.error('Error updating chat title', LogCategory.DATABASE, { error, chatId });
      return null;
    }
  }

  /**
   * Delete a chat and all its messages
   * @param chatId The ID of the chat to delete
   * @returns True if successful, false otherwise
   */
  async deleteChat(chatId: string): Promise<boolean> {
    try {
      // Delete all messages first
      const { error: messagesError } = await supabase
        .from(this.TABLE_CHAT_MESSAGES)
        .delete()
        .eq('chat_wrapper_id', chatId);
      
      if (messagesError) {
        logger.error('Failed to delete chat messages', LogCategory.DATABASE, { error: messagesError, chatId });
        return false;
      }
      
      // Then delete the chat wrapper
      const { error: chatError } = await supabase
        .from(this.TABLE_CHAT_WRAPPER)
        .delete()
        .eq('id', chatId);
      
      if (chatError) {
        logger.error('Failed to delete chat wrapper', LogCategory.DATABASE, { error: chatError, chatId });
        return false;
      }
      
      logger.info('Chat deleted successfully', LogCategory.DATABASE, { chatId });
      return true;
    } catch (error) {
      logger.error('Error deleting chat', LogCategory.DATABASE, { error, chatId });
      return false;
    }
  }

  /**
   * Subscribe to new messages for a specific chat using Supabase realtime
   * @param chatWrapperId The ID of the chat to subscribe to
   * @param callback Function to call when a new message is received
   * @returns A subscription object with an unsubscribe method
   */
  subscribeToMessages(chatWrapperId: string, callback: (message: ChatMessage) => void) {
    try {
      console.log('[TEST] ChatService - Setting up realtime subscription for chat:', chatWrapperId);
      logger.info('Starting realtime subscription for chat', LogCategory.DATABASE, { chatWrapperId });
      
      // Track the last message ID to avoid duplicate notifications
      let lastMessageId: string | null = null;
      let isFirstSubscription = true;
      
      // Create a unique channel name using the chat ID to avoid conflicts
      const channelName = `chat-messages-${chatWrapperId}`;
      console.log('[TEST] ChatService - Using channel name:', channelName);
      
      // Set up Supabase realtime subscription with more robust configuration
      const subscription = supabase
        .channel(channelName)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: this.TABLE_CHAT_MESSAGES,
            filter: `chat_wrapper_id=eq.${chatWrapperId}`
          },
          (payload) => {
            try {
              const newMessage = payload.new as ChatMessage;
              
              // Skip if this is a message we've already processed
              if (lastMessageId === newMessage.id) {
                console.log('[TEST] ChatService - Skipping duplicate message:', newMessage.id);
                return;
              }
              
              console.log('[TEST] ChatService - New message received via realtime:', newMessage.id);
              console.log('[TEST] ChatService - Message type:', newMessage.is_ai_message ? 'AI' : 'User');
              
              // Update last message ID
              lastMessageId = newMessage.id;
              
              // Log the event
              logger.info('Received new message from realtime subscription', LogCategory.DATABASE, {
                chatWrapperId,
                messageId: newMessage.id
              });
              
              // Call callback with the new message
              callback(newMessage);
            } catch (err) {
              console.error('[TEST] ChatService - Error processing realtime message:', err);
              logger.error('Error processing realtime message', LogCategory.DATABASE, { error: err, chatWrapperId });
            }
          }
        )
        .subscribe((status, data) => {
          console.log('[TEST] ChatService - Realtime subscription status:', status);
          console.log('[TEST] ChatService - data:', data);

          // Handle subscription status
          if (status === 'SUBSCRIBED') {
            console.log('[TEST] ChatService - Successfully subscribed to realtime updates');
            
            // If this is the first subscription, fetch the latest message ID to avoid duplicates
            if (isFirstSubscription) {
              isFirstSubscription = false;
              
              // Fetch the latest message to get its ID
              (async () => {
                try {
                  const { data, error } = await supabase
                    .from(this.TABLE_CHAT_MESSAGES)
                    .select('id')
                    .eq('chat_wrapper_id', chatWrapperId)
                    .order('created_at', { ascending: false })
                    .limit(1)
                    .single();
                  
                  if (!error && data) {
                    lastMessageId = data.id;
                    console.log('[TEST] ChatService - Initial message ID captured:', lastMessageId);
                  }
                } catch (err) {
                  console.error('[TEST] ChatService - Error fetching initial message:', err);
                }
              })();
            }
          } else if (status === 'TIMED_OUT') {
            console.error('[TEST] ChatService - Subscription timed out');
            logger.error('Supabase realtime subscription timed out', LogCategory.DATABASE, { chatWrapperId });
          }
        });
            
      // Return unsubscribe function
      return {
        unsubscribe: () => {
          console.log('[TEST] ChatService - Unsubscribing from realtime for chat:', chatWrapperId);
          logger.info('Stopping realtime subscription', LogCategory.DATABASE, { chatWrapperId });
                    
          // Remove the subscription
          supabase.removeChannel(subscription);
        }
      };
    } catch (error) {
      logger.error('Error setting up realtime subscription', LogCategory.DATABASE, { error, chatWrapperId });
      // Return a dummy unsubscribe function to prevent errors
      return {
        unsubscribe: () => {
          console.log('[TEST] ChatService - Dummy unsubscribe called due to setup error');
        }
      };
    }
  }
}

// Export a singleton instance
export const chatService = new ChatService();